// Import core React libraries
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import {
  FaCreditCard,
  FaUniversity,
  FaRegMoneyBillAlt,
  FaExclamation,
  FaRegEdit,
} from "react-icons/fa";
import { MdToday, MdPerson, MdLocationCity } from "react-icons/md";
import { NavLink } from "react-router-dom";

import { displayNextDonationDate } from "../../helpers/Donations";

import * as select from "../../reducers";
import * as actions from "../../actions/modals";

import "../../styles/components/_programs.scss";
import moment from "moment";

function ProgramDetail({
  donation_recipient,
  donation_location,
  charity_avatar,
  charity_type,
  donation_schedule,
  donation_value,
  payment_type,
  debit_date,
  creditCard,
  onSelect,
  navlinks,
  donation_batch_type,
}) {
  const shouldShowArelt = useMemo(() => {
    const has_credit_card =
      typeof creditCard !== "undefined" && Object.keys(creditCard).length > 0;
    let show_alert = false;
    let date_next = "";

    // If this donation is assigned to a credit card...
    if (donation_batch_type === "CC" && has_credit_card) {
      // Display an alert if the next donation date is after the credit cards expiry date.
      date_next = displayNextDonationDate(
        { debit_date, donation_schedule },
        "YYYY-MM"
      );
      // console.log({ date_next });
      show_alert = moment(date_next, "YYYY-MM").isSameOrAfter(
        moment(creditCard.expiry_date, "YYYY-MM"),
        "M"
      );
    }

    // If this donation is assigned to a credit card, yet there is no credit card for the account...
    if (donation_batch_type === "CC" && has_credit_card === false) {
      // Display an alert
      show_alert = true;
    }

    return show_alert;
  }, [creditCard, debit_date, donation_batch_type, donation_schedule]);

  const renderAvatar = () => {
    const placeholderAvatar =
      charity_type.toLowerCase() === "person" ? (
        <div className="program__profile-pic">
          <MdPerson size={120} />
        </div>
      ) : (
        <div className="program__profile-pic">
          <MdLocationCity size={120} />
        </div>
      );

    return charity_avatar ? (
      <img
        className="program__profile-pic"
        src={`data:image/png;base64,${charity_avatar}`}
        alt="cherity_avatar"
      ></img>
    ) : (
      placeholderAvatar
    );
  };

  const show_next_donation = payment_type !== "Direct Deposit";

  return (
    <div className="program" onClick={onSelect}>
      <div className="program__wrap">
        <FaRegEdit className="program__edit-icon" size={24} />
        <div className="program__content">
          {renderAvatar()}
          <h2 className="program__recipient">{donation_recipient}</h2>
          <p className="mb--24 program__location">{donation_location}</p>

          <small className={show_next_donation === true ? "" : "hide"}>
            Next Donation
          </small>
          <p className={show_next_donation === true ? "" : "hide"}>
            <span className="program__next-donation-date">
              <MdToday size={16} />
            </span>{" "}
            {displayNextDonationDate({ debit_date, donation_schedule })}
          </p>
        </div>
        <div className="program__footer">
          <p className="program__footer--amount">
            <span className="program__footer--dollar">$</span>
            <span className="program__footer--value">{donation_value}</span>
            <span className="program__footer--frequency">
              {donation_schedule}
            </span>
          </p>
          {payment_type === "Credit Card" && (
            <FaCreditCard className="program__footer--type" size={24} />
          )}
          {payment_type === "Direct Debit" && (
            <FaUniversity className="program__footer--type" size={24} />
          )}
          {payment_type === "Direct Deposit" && (
            <FaRegMoneyBillAlt className="program__footer--type" size={24} />
          )}
          <AlertDropdown navlinks={navlinks} userDetails={shouldShowArelt} />
        </div>
        {/* {payment_type} */}
      </div>
    </div>
  );
}

/**
 * Render an alert modal informing the user what steps to take to correct the expiring credit card.
 * @extends Component
 */
function AlertDropdown({ userDetails }) {
  const [showAlertDropdown, setShowAlertDropdown] = useState(false);
  const toggleAlertDropdown = (event) => {
    event.stopPropagation();
    setShowAlertDropdown((prev) => !prev);
  };

  if (!userDetails) {
    return null;
  }

  return (
    <div
      className={`nav__alert left ${
        showAlertDropdown ? "is-open" : "is-closed"
      }`}
    >
      <FaExclamation size={10} onClick={toggleAlertDropdown} />
      <div className="fancy-dropdown__menu tooltip-modal cc-expiry">
        <h2 className="h2--dark mt--16">
          Your credit card will expire before the next donation date.
        </h2>
        <p>If you do not act, your financial support will cease.</p>
        <ol className="cc-expiry__list">
          <li>
            Please{" "}
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `cc-expiry__item ${isActive ? "active" : ""}`
              }
              onClick={toggleAlertDropdown}
            >
              update your credit card details
            </NavLink>
          </li>
          <li>
            or{" "}
            <NavLink
              className={({ isActive }) =>
                `cc-expiry__item ${isActive ? "active" : ""}`
              }
              to="/settings"
              onClick={toggleAlertDropdown}
            >
              change your method of giving
            </NavLink>
          </li>
          <li>
            or{" "}
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `cc-expiry__item ${isActive ? "active" : ""}`
              }
              onClick={toggleAlertDropdown}
            >
              cease your financial support
            </NavLink>
          </li>
        </ol>
      </div>
      <div
        className={`modal ${
          showAlertDropdown ? "modal--open" : "modal--closed"
        }`}
        onClick={toggleAlertDropdown}
      ></div>
    </div>
  );
}

ProgramDetail.propTypes = {
  donation_recipient: PropTypes.string.isRequired,
  donation_location: PropTypes.string,
  charity_avatar: PropTypes.string,
  donation_schedule: PropTypes.string.isRequired,
  donation_value: PropTypes.string.isRequired,
  payment_type: PropTypes.string.isRequired,
  debit_date: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  return {
    programs: select.getPrograms(state),
    creditCard: select.getSettingsCreditCard(state),
  };
};

export default connect(mapStateToProps, actions)(ProgramDetail);
